// =====================================================
// 📁 PendingSaleCard.tsx
// =====================================================

import React from 'react';
import { ArrowRight, FileText, X } from 'lucide-react';

import { PendingSaleSummary } from '../../../../../types/sale';
import { useSale } from '../../../../../hooks/sale/useSale';
import { useSalesButtonActions } from '../../../../../hooks/sale/useSalesButtonConfig';

import ActionButton from '../../../../Buttons/ActionButton';
import Can from '../../../../auth/Can';

interface Props {
  sale: PendingSaleSummary;
}

const PendingSaleCard: React.FC<Props> = ({ sale }) => {
  const { handlerGetSaleToContinue } = useSale();

  const { handlerCancelPendingSale } = useSalesButtonActions();

  return (
    <div
      className="
        overflow-hidden

        rounded-xl

        border
        border-stroke

        bg-white

        shadow-1

        transition-all
        duration-200

        hover:border-primary/20

        dark:border-strokedark
        dark:bg-boxdark
      "
    >
      {/* ===================================================== */}
      {/* Header */}
      {/* ===================================================== */}

      <div
        className="
          flex
          flex-col
          gap-4

          p-4

          dark:bg-black

          sm:flex-row
          sm:items-center
          sm:justify-between
        "
      >
        {/* ===================================================== */}
        {/* Left */}
        {/* ===================================================== */}

        <div
          className="
            flex
            min-w-0
            flex-1
            items-start
            gap-3

            sm:items-center
          "
        >
          <div
            className="
              flex
              h-10
              w-10
              shrink-0
              items-center
              justify-center

              rounded-xl

              bg-primary/10

              text-primary
            "
          >
            <FileText size={18} />
          </div>

          <div className="min-w-0 flex-1">
            <div
              className="
                flex
                flex-wrap
                items-center
                gap-2
              "
            >
              <h4
                className="
                  truncate

                  text-sm
                  font-bold

                  text-primary
                "
              >
                {sale.documentType}
              </h4>

              <span
                className="
                  rounded-full

                  bg-primary/10

                  px-2
                  py-0.5

                  text-[10px]
                  font-semibold

                  text-primary
                "
              >
                Pendiente
              </span>
            </div>

            <p
              className="
                mt-1

                break-words

                text-sm
                font-semibold

                text-black

                dark:text-white
              "
            >
              {sale.customerName}
            </p>
          </div>
        </div>

        {/* ===================================================== */}
        {/* Right */}
        {/* ===================================================== */}

        <div
          className="
            flex
            items-end
            justify-between

            border-t
            border-stroke

            pt-3

            dark:border-strokedark

            sm:ml-4
            sm:block
            sm:shrink-0
            sm:border-0
            sm:pt-0
            sm:text-right
          "
        >
          <div>
            <p
              className="
                text-[10px]
                uppercase
                tracking-wide

                text-body
              "
            >
              Total
            </p>

            <p
              className="
                text-lg
                font-bold

                leading-none

                text-primary

                sm:text-title-xsm
              "
            >
              ${sale.total}
            </p>
          </div>

          <p
            className="
              text-xs
              font-medium

              text-body

              sm:mt-1
            "
          >
            {sale.saleDate}
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* Actions */}
      {/* ===================================================== */}

      <div
        className="
          flex
          flex-col
          gap-2

          border-t
          border-stroke

          bg-whiten

          p-3

          dark:border-strokedark
          dark:bg-boxdark-2

          sm:flex-row
          sm:justify-end
        "
      >
        <div className="w-full sm:w-auto">
          <ActionButton
            label="Continuar"
            icon={<ArrowRight size={14} />}
            variant="primary"
            fullWidth
            onClick={() => handlerGetSaleToContinue(sale.id)}
          />
        </div>

        <Can perm="SALES_CANCEL">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Cancelar"
              icon={<X size={14} />}
              variant="danger"
              fullWidth
              onClick={() => handlerCancelPendingSale(sale.id)}
            />
          </div>
        </Can>
      </div>
    </div>
  );
};

export default PendingSaleCard;
